import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Chip,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import {
  ArrowRight,
  Users,
  Zap,
  TrendingUp,
  Settings,
  CheckCircle2,
  XCircle,
  Shield,
  X,
  ChevronDown,
  ChevronUp,
  Mail,
} from 'lucide-react';
import { StageComparisonFrame } from '../components/StageComparisonFrame';
import { SystemGraphic } from '../components/SystemGraphic';

export default function HomePage() {
  const navigate = useNavigate();
  const [visiblePills, setVisiblePills] = useState([true, true, true]);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  
  const scrollToHowWeHelp = () => {
    const element = document.getElementById('how-prod-release-helps');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleDeletePill = (index: number) => {
    setVisiblePills(prev => {
      const newState = [...prev];
      newState[index] = false;
      return newState;
    });
  };

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };
  
  const stageServices = [
    {
      icon: Users,
      stage: 'PRE-SEED',
      title: 'Foundation & Team Build',
      description:
        'Setting up your engineering team, processes, and initial product development.',
      subtitle: 'Build your founding team and establish engineering foundations',
      duration: '4-8 weeks',
      pricing: '$10K-$25K total',
      keyFeatures: [
        'Tech stack selection & architecture design',
        'Engineering process & culture setup',
        'Founding engineer interview support',
        'Development tooling & workflow setup',
      ],
      href: '/contact',
      stageColor: '#3B82F6',
      stageNumber: 1,
    },
    {
      icon: Zap,
      stage: 'SEED',
      title: 'Streamline Development Process',
      subtitle: 'Scale your delivery with experienced engineering leadership and proven processes',
      duration: '3-6 months',
      pricing: '$15K-$35K/month',
      keyFeatures: [
        'Fractional CTO/VP Engineering leadership',
        'Sprint planning & delivery acceleration',
        'Engineering processes & team rituals',
        'Technical roadmap development',
        'Team performance optimization',
      ],
      href: '/contact',
      stageColor: '#F59E0B',
      stageNumber: 2,
    },
    {
      icon: TrendingUp,
      stage: 'SERIES A',
      title: 'Audit for Best Practices',
      subtitle: 'Comprehensive audit of your engineering practices, architecture, and processes for optimal performance',
      duration: '6-12 weeks',
      pricing: '$25K-$75K total',
      keyFeatures: [
        'Comprehensive architecture audit & review',
        'External code review & quality assessment',
        'Security audit & vulnerability analysis',
        'Infrastructure & cloud optimization review',
        'Engineering processes & best practices evaluation',
      ],
      href: '/contact',
      stageColor: '#8B5CF6',
      stageNumber: 3,
    },
  ];

  // const problems = [
  //   {
  //     icon: Users,
  //     title: 'Hiring Skilled Engineers Fast',
  //     description: 'Startups struggle to find senior engineers, architects, and product-focused developers who understand speed, quality, and scale.',
  //     pain: 'Slow hiring delays product launch → missed market timing.',
  //     color: '#1EB980',
  //   },
  //   {
  //     icon: Layers,
  //     title: 'Choosing the Right Architecture Early',
  //     description: 'Microservices vs monolith, Serverless vs containers, SQL vs NoSQL, Events vs APIs.',
  //     pain: 'Wrong choices = years of inefficiency.',
  //     color: '#1EB980',
  //   },
  //   {
  //     icon: Target,
  //     title: 'Building the First Version Right (MVP → Scalable MVP)',
  //     description: 'Most MVPs become messy prototypes. Startups fail when they can\'t convert MVPs into scalable products.',
  //     pain: 'Rewrites cost 3× more in year 2.',
  //     color: '#1EB980',
  //   },
  //   {
  //     icon: AlertCircle,
  //     title: 'Lack of Technical Leadership (No CTO / Weak Tech Vision)',
  //     description: 'Founders without strong tech leadership make poor architectural decisions that block growth.',
  //     pain: 'Wrong stack choices → performance issues → high cost.',
  //     color: '#1EB980',
  //   },
  //   {
  //     icon: Zap,
  //     title: 'Shipping Fast Without Breaking Things',
  //     description: 'Engineering teams struggle with balancing speed, quality, testing, DevOps, and documentation.',
  //     pain: 'Velocity drops after 6–12 months due to tech debt.',
  //     color: '#1EB980',
  //   },
  //   {
  //     icon: TrendingUp,
  //     title: 'Scaling Infrastructure Efficiently',
  //     description: 'They overspend on cloud OR build systems that crash when user count grows.',
  //     pain: 'Either slow product OR painful cloud bills.',
  //     color: '#1EB980',
  //   },
  //   {
  //     icon: GitMerge,
  //     title: 'Product–Engineering Misalignment',
  //     description: 'Founders want speed. Engineers want stability. This creates friction.',
  //     pain: 'Features are built but user value doesn\'t increase.',
  //     color: '#1EB980',
  //   },
  //   {
  //     icon: Settings,
  //     title: 'Running Reliable Delivery Processes',
  //     description: 'No clear process: sprint planning, QA, testing, CI/CD, code reviews, ownership.',
  //     pain: 'Poor execution → inconsistent releases → bugs.',
  //     color: '#1EB980',
  //   },
  //   {
  //     icon: DollarSign,
  //     title: 'Managing Cost-Efficient Development',
  //     description: 'Startups burn money on: Excessive cloud costs, Overhiring, Low productivity teams, Poor architecture decisions.',
  //     pain: 'High burn rate → pressure from investors.',
  //     color: '#1EB980',
  //   },
  //   {
  //     icon: TrendingUp,
  //     title: 'User Growth Doesn\'t Match Engineering Complexity',
  //     description: 'Startups struggle when product grows faster than team maturity.',
  //     pain: 'Features slow down, reliability drops.',
  //     color: '#1EB980',
  //   },
  // ];

  // const testimonials = [
  //   {
  //     quote:
  //       'prodrelease helped us go from idea to production in 8 weeks. Their technical guidance was invaluable.',
  //     author: 'Sarah Kim',
  //     title: 'Founder, TechStartup',
  //     company: 'YC S23',
  //     initials: 'SK',
  //     photo: 'https://images.unsplash.com/photo-1600275669283-4bf2bb8a990c?w=200',
  //   },
  //   {
  //     quote:
  //       'Best decision we made. We got senior-level architecture expertise without the senior-level salary.',
  //     author: 'Michael Chen',
  //     title: 'CTO, FinanceApp',
  //     company: 'Seed Stage',
  //     initials: 'MC',
  //     photo: 'https://images.unsplash.com/photo-1598268012815-ae21095df31b?w=200',
  //   },
  //   {
  //     quote:
  //       'They built our engineering culture from scratch. The processes they implemented scaled with us from 2 to 15 engineers.',
  //     author: 'Jessica Park',
  //     title: 'CEO, DataFlow',
  //     company: 'Series A',
  //     initials: 'JP',
  //     photo: 'https://images.unsplash.com/photo-1573496358961-3c82861ab8f4?w=200',
  //   },
  //   {
  //     quote:
  //       'Their fractional CTO service gave us the leadership we needed without breaking our budget. Game changer.',
  //     author: 'David Martinez',
  //     title: 'Founder, CloudSync',
  //     company: 'Pre-Seed',
  //     initials: 'DM',
  //     photo: 'https://images.unsplash.com/photo-1762522927402-f390672558d8?w=200',
  //   },
  // ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'background.default',
          pt: { xs: 8, md: 12 },
          pb: { xs: 10, md: 15 },
        }}
      >
        <Container maxWidth="xl">
          <Box 
            sx={{ 
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: { xs: 6, md: 8 },
              maxWidth: 1400,
              mx: 'auto',
            }}
          >
            {/* Left Side - Text Content */}
            <Box 
              sx={{ 
                flex: 1,
                textAlign: { xs: 'center', md: 'left' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'flex-start' },
              }}
            >
            {/* Top Badge */}
            <Chip
              label="Engineering Advisory for Startups"
              sx={{
                bgcolor: 'rgba(30, 185, 128, 0.1)',
                color: 'primary.main',
                fontWeight: 600,
                px: 2,
                py: 2.5,
                mb: 4,
              }}
            />
            
            {/* Main Headline */}
            <Typography 
              variant="heroHeadline"
              sx={{ 
                mb: 4,
                color: 'text.primary',
                textTransform: 'uppercase',
              }}
            >
              We empower tech startup's growth
            </Typography>
            
            {/* Subtitle */}
            <Typography 
              variant="h5" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 600, 
                mb: 5,
                lineHeight: 1.6,
              }}
            >
              Build and scale a high-performing engineering org that ships fast, scales reliably, and meets investor expectations — without hiring a full-time CTO
            </Typography>
            
            {/* Stage Chips */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: { xs: 'center', md: 'flex-start' },
                alignItems: 'center',
                mb: 5,
              }}
            >
              <Chip
                label="Hire Founding Team"
                onClick={scrollToHowWeHelp}
                sx={{
                  bgcolor: 'rgba(59, 130, 246, 0.15)',
                  color: '#3B82F6',
                  fontWeight: 600,
                  px: 3,
                  py: 3,
                  height: 'auto',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(59, 130, 246, 0.25)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(59, 130, 246, 0.3)',
                  },
                  '& .MuiChip-label': {
                    px: 1,
                  },
                }}
              />
              <Chip
                label="Streamline Development Process"
                onClick={scrollToHowWeHelp}
                sx={{
                  bgcolor: 'rgba(245, 158, 11, 0.15)',
                  color: '#F59E0B',
                  fontWeight: 600,
                  px: 3,
                  py: 3,
                  height: 'auto',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(245, 158, 11, 0.25)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(245, 158, 11, 0.3)',
                  },
                  '& .MuiChip-label': {
                    px: 1,
                  },
                }}
              />
              <Chip
                label="Audit for Best Practices"
                onClick={scrollToHowWeHelp}
                sx={{
                  bgcolor: 'rgba(139, 92, 246, 0.15)',
                  color: '#8B5CF6',
                  fontWeight: 600,
                  px: 3,
                  py: 3,
                  height: 'auto',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(139, 92, 246, 0.25)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(139, 92, 246, 0.3)',
                  },
                  '& .MuiChip-label': {
                    px: 1,
                  },
                }}
              />
            </Box>

            {/* Team Photos with Trust Badge */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' }, mt: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {[
                  'https://images.unsplash.com/photo-1758524056096-6bedf35a33f4?w=200',
                  'https://images.unsplash.com/photo-1600275669283-4bf2bb8a990c?w=200',
                  'https://images.unsplash.com/photo-1598268012815-ae21095df31b?w=200',
                  'https://images.unsplash.com/photo-1589114207353-1fc98a11070b?w=200',
                  'https://images.unsplash.com/photo-1762522927402-f390672558d8?w=200',
                ].map((photo, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: { xs: 44, md: 52 },
                      height: { xs: 44, md: 52 },
                      borderRadius: '50%',
                      border: '3px solid',
                      borderColor: 'background.default',
                      overflow: 'hidden',
                      ml: index > 0 ? -1.5 : 0,
                      position: 'relative',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px) scale(1.1)',
                        zIndex: 10,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={photo}
                      alt={`Team member ${index + 1}`}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                ))}
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}>
                  Trusted by 50+ startups
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  YC, Techstars & more
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Right Side - Hero Image */}
          <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' }, position: 'relative', maxWidth: 600 }}>
            <Box sx={{ position: 'relative', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(30, 185, 128, 0.3)', border: '2px solid rgba(30, 185, 128, 0.2)' }}>
              <Box component="img" src="https://images.unsplash.com/photo-1758876203342-fc14c0bba67c?w=800" alt="Engineering team collaboration" sx={{ width: '100%', height: 'auto', display: 'block' }} />
              <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '50%', background: 'linear-gradient(to top, rgba(0, 0, 0, 0.4), transparent)' }} />
            </Box>

            <Paper elevation={0} sx={{ position: 'absolute', bottom: -30, right: -30, p: 3, borderRadius: '16px', bgcolor: 'background.paper', border: '2px solid', borderColor: 'rgba(30, 185, 128, 0.3)', boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)', backdropFilter: 'blur(10px)', minWidth: 220 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: 'rgba(30, 185, 128, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckCircle2 size={28} color="#1EB980" strokeWidth={2.5} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '1.75rem', fontWeight: 900, background: 'linear-gradient(135deg, #1EB980 0%, #22c997 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', lineHeight: 1 }}>
                    30-45
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Days to Transform
                  </Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.5 }}>
                From chaotic to delivery machine
              </Typography>
            </Paper>

            <Box sx={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(30, 185, 128, 0.15) 0%, transparent 70%)', filter: 'blur(40px)', pointerEvents: 'none', zIndex: -1 }} />
          </Box>
          </Box>
        </Container>
      </Box>

      {/* CTA Section - "It needs a SYSTEM" + Guarantee - MERGED */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          py: { xs: 10, md: 14 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            background: 'radial-gradient(circle at 20% 50%, rgba(30, 185, 128, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(85, 255, 225, 0.2) 0%, transparent 50%)',
          }}
        />
        
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 1200, mx: 'auto', position: 'relative', zIndex: 1 }}>
            
            {/* Redesigned Section - Text + SVG Side by Side */}
            <Box 
              sx={{ 
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 5, md: 8 },
                alignItems: 'center',
                mb: 8,
              }}
            >
              {/* Left Side - Text Content */}
              <Box 
                sx={{ 
                  flex: 1,
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                {/* Your startup text */}
                <Typography 
                  sx={{ 
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 2.5,
                    lineHeight: 1.3,
                  }}
                >
                  Your startup
                </Typography>
                
                {/* Three "doesn't need" lines as larger pills */}
                <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1, mb: 4, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  {visiblePills[0] && (
                    <Box
                      onClick={() => handleDeletePill(0)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1.2,
                        py: 0.4,
                        borderRadius: '6px',
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : '#FEE2E2',
                        border: (theme) => theme.palette.mode === 'dark' ? '2px solid rgba(239, 68, 68, 0.4)' : '2px solid #FCA5A5',
                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                        fontWeight: 600,
                        color: (theme) => theme.palette.mode === 'dark' ? '#FCA5A5' : '#991B1B',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: (theme) => theme.palette.mode === 'dark' 
                          ? '0 2px 6px rgba(220, 38, 38, 0.2)' 
                          : '0 2px 6px rgba(220, 38, 38, 0.1)',
                        mx: { xs: 'auto', md: 0 },
                        maxWidth: 'fit-content',
                        '&:hover': {
                          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.25)' : '#FECACA',
                          transform: 'translateX(-2px)',
                          boxShadow: (theme) => theme.palette.mode === 'dark' 
                            ? '0 3px 8px rgba(220, 38, 38, 0.3)' 
                            : '0 3px 8px rgba(220, 38, 38, 0.2)',
                        },
                      }}
                    >
                      doesn&apos;t need more developers
                      <X size={12} strokeWidth={2.5} />
                    </Box>
                  )}
                  
                  {visiblePills[1] && (
                    <Box
                      onClick={() => handleDeletePill(1)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1.2,
                        py: 0.4,
                        borderRadius: '6px',
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : '#FEE2E2',
                        border: (theme) => theme.palette.mode === 'dark' ? '2px solid rgba(239, 68, 68, 0.4)' : '2px solid #FCA5A5',
                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                        fontWeight: 600,
                        color: (theme) => theme.palette.mode === 'dark' ? '#FCA5A5' : '#991B1B',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: (theme) => theme.palette.mode === 'dark' 
                          ? '0 2px 6px rgba(220, 38, 38, 0.2)' 
                          : '0 2px 6px rgba(220, 38, 38, 0.1)',
                        mx: { xs: 'auto', md: 0 },
                        maxWidth: 'fit-content',
                        '&:hover': {
                          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.25)' : '#FECACA',
                          transform: 'translateX(-2px)',
                          boxShadow: (theme) => theme.palette.mode === 'dark' 
                            ? '0 3px 8px rgba(220, 38, 38, 0.3)' 
                            : '0 3px 8px rgba(220, 38, 38, 0.2)',
                        },
                      }}
                    >
                      doesn&apos;t need more tools
                      <X size={12} strokeWidth={2.5} />
                    </Box>
                  )}
                  
                  {visiblePills[2] && (
                    <Box
                      onClick={() => handleDeletePill(2)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1.2,
                        py: 0.4,
                        borderRadius: '6px',
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : '#FEE2E2',
                        border: (theme) => theme.palette.mode === 'dark' ? '2px solid rgba(239, 68, 68, 0.4)' : '2px solid #FCA5A5',
                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                        fontWeight: 600,
                        color: (theme) => theme.palette.mode === 'dark' ? '#FCA5A5' : '#991B1B',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: (theme) => theme.palette.mode === 'dark' 
                          ? '0 2px 6px rgba(220, 38, 38, 0.2)' 
                          : '0 2px 6px rgba(220, 38, 38, 0.1)',
                        mx: { xs: 'auto', md: 0 },
                        maxWidth: 'fit-content',
                        '&:hover': {
                          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.25)' : '#FECACA',
                          transform: 'translateX(-2px)',
                          boxShadow: (theme) => theme.palette.mode === 'dark' 
                            ? '0 3px 8px rgba(220, 38, 38, 0.3)' 
                            : '0 3px 8px rgba(220, 38, 38, 0.2)',
                        },
                      }}
                    >
                      doesn&apos;t need more guesses
                      <X size={12} strokeWidth={2.5} />
                    </Box>
                  )}
                </Box>

                {/* Big Reveal Text */}
                <Typography 
                  sx={{ 
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 900,
                    color: 'white', 
                    textTransform: 'uppercase',
                    mb: 3,
                    lineHeight: 1,
                    background: 'linear-gradient(135deg, #1EB980 0%, #55FFE1 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  It needs a SYSTEM.
                </Typography>

                {/* Guarantee Badge */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 3,
                    py: 1.5,
                    borderRadius: '12px',
                    bgcolor: 'rgba(30, 185, 128, 0.12)',
                    border: '2px solid rgba(30, 185, 128, 0.4)',
                    boxShadow: '0 4px 20px rgba(30, 185, 128, 0.25)',
                    mx: { xs: 'auto', md: 0 },
                  }}
                >
                  <Shield size={22} color="#1EB980" strokeWidth={2.5} />
                  <Typography
                    sx={{
                      fontSize: { xs: '0.95rem', md: '1.05rem' },
                      fontWeight: 700,
                      color: 'text.primary',
                      lineHeight: 1.4,
                    }}
                  >
                    We build that system in{' '}
                    <Box component="span" sx={{ color: '#1EB980', fontWeight: 900 }}>
                      30-45 days. Guaranteed.
                    </Box>
                  </Typography>
                </Box>
              </Box>

              {/* Right Side - SVG Graphic with Floating Stats */}
              <Box 
                sx={{ 
                  flex: 1,
                  position: 'relative',
                  maxWidth: { xs: '100%', md: 550 },
                  width: '100%',
                }}
              >
                <Box 
                  sx={{ 
                    position: 'relative', 
                    borderRadius: '24px', 
                    overflow: 'hidden', 
                    boxShadow: '0 20px 60px rgba(30, 185, 128, 0.4)', 
                    border: '3px solid rgba(30, 185, 128, 0.3)',
                    transition: 'transform 0.3s ease',
                    bgcolor: 'rgba(15, 23, 42, 0.6)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 28px 70px rgba(30, 185, 128, 0.5)',
                    },
                  }}
                >
                  <SystemGraphic />
                </Box>

              {/* Floating Stat Cards */}
              <Paper 
                elevation={0} 
                sx={{ 
                  position: 'absolute', 
                  top: { xs: -15, md: -25 }, 
                  right: { xs: -10, md: -30 }, 
                  p: { xs: 2, md: 2.5 }, 
                  borderRadius: '16px', 
                  bgcolor: 'background.paper', 
                  border: '2px solid', 
                  borderColor: 'rgba(30, 185, 128, 0.3)', 
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)', 
                  backdropFilter: 'blur(10px)',
                  minWidth: { xs: 140, md: 180 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box 
                    sx={{ 
                      width: { xs: 40, md: 48 }, 
                      height: { xs: 40, md: 48 }, 
                      borderRadius: '12px', 
                      bgcolor: 'rgba(30, 185, 128, 0.15)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                    }}
                  >
                    <Settings size={24} color="#1EB980" strokeWidth={2.5} />
                  </Box>
                  <Box>
                    <Typography 
                      sx={{ 
                        fontSize: { xs: '1.4rem', md: '1.75rem' }, 
                        fontWeight: 900, 
                        background: 'linear-gradient(135deg, #1EB980 0%, #22c997 100%)', 
                        WebkitBackgroundClip: 'text', 
                        WebkitTextFillColor: 'transparent', 
                        backgroundClip: 'text', 
                        lineHeight: 1,
                      }}
                    >
                      3x
                    </Typography>
                    <Typography 
                      sx={{ 
                        fontSize: { xs: '0.65rem', md: '0.75rem' }, 
                        fontWeight: 600, 
                        color: 'text.secondary', 
                        textTransform: 'uppercase', 
                        letterSpacing: '0.05em',
                      }}
                    >
                      Faster Delivery
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper 
                elevation={0} 
                sx={{ 
                  position: 'absolute', 
                  bottom: { xs: -15, md: -25 }, 
                  left: { xs: -10, md: -30 }, 
                  p: { xs: 2, md: 2.5 }, 
                  borderRadius: '16px', 
                  bgcolor: 'background.paper', 
                  border: '2px solid', 
                  borderColor: 'rgba(85, 255, 225, 0.3)', 
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)', 
                  backdropFilter: 'blur(10px)',
                  minWidth: { xs: 140, md: 180 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box 
                    sx={{ 
                      width: { xs: 40, md: 48 }, 
                      height: { xs: 40, md: 48 }, 
                      borderRadius: '12px', 
                      bgcolor: 'rgba(85, 255, 225, 0.15)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                    }}
                  >
                    <CheckCircle2 size={24} color="#55FFE1" strokeWidth={2.5} />
                  </Box>
                  <Box>
                    <Typography 
                      sx={{ 
                        fontSize: { xs: '1.4rem', md: '1.75rem' }, 
                        fontWeight: 900, 
                        background: 'linear-gradient(135deg, #55FFE1 0%, #1EB980 100%)', 
                        WebkitBackgroundClip: 'text', 
                        WebkitTextFillColor: 'transparent', 
                        backgroundClip: 'text', 
                        lineHeight: 1,
                      }}
                    >
                      95%
                    </Typography>
                    <Typography 
                      sx={{ 
                        fontSize: { xs: '0.65rem', md: '0.75rem' }, 
                        fontWeight: 600, 
                        color: 'text.secondary', 
                        textTransform: 'uppercase', 
                        letterSpacing: '0.05em',
                      }}
                    >
                      Success Rate
                    </Typography>
                  </Box>
                </Box>
              </Paper>
              </Box>
            </Box>

            {/* Compact Stats Row */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 3, md: 4 },
                justifyContent: 'center',
                alignItems: 'center',
                mb: 6,
              }}
            >
              <Box sx={{ textAlign: 'center', maxWidth: 200 }}>
                <Typography
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 900,
                    lineHeight: 1,
                    background: 'linear-gradient(135deg, #1EB980 0%, #22c997 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    mb: 0.5,
                  }}
                >
                  30-45
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    mb: 1,
                  }}
                >
                  Days
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    lineHeight: 1.4,
                  }}
                >
                  To transform your team into a delivery machine
                </Typography>
              </Box>

              <Box
                sx={{
                  width: { xs: '60%', md: '2px' },
                  height: { xs: '2px', md: '80px' },
                  bgcolor: 'divider',
                }}
              />

              <Box sx={{ textAlign: 'center', maxWidth: 200 }}>
                <Typography
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 900,
                    lineHeight: 1,
                    background: 'linear-gradient(135deg, #1EB980 0%, #22c997 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    mb: 0.5,
                  }}
                >
                  $0
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    mb: 1,
                  }}
                >
                  Extra Cost
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    lineHeight: 1.4,
                  }}
                >
                  If we don't deliver, we keep working until we do
                </Typography>
              </Box>

              <Box
                sx={{
                  width: { xs: '60%', md: '2px' },
                  height: { xs: '2px', md: '80px' },
                  bgcolor: 'divider',
                }}
              />

              <Box sx={{ textAlign: 'center', maxWidth: 200 }}>
                <Typography
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 900,
                    lineHeight: 1,
                    background: 'linear-gradient(135deg, #1EB980 0%, #22c997 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    mb: 0.5,
                  }}
                >
                  100%
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    mb: 1,
                  }}
                >
                  Commitment
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    lineHeight: 1.4,
                  }}
                >
                  We're all-in on making your engineering team succeed
                </Typography>
              </Box>
            </Box>

            {/* Confidence Statement */}
            <Typography
              sx={{
                fontSize: { xs: '0.95rem', md: '1.05rem' },
                color: 'text.primary',
                maxWidth: 650,
                mx: 'auto',
                mb: 5,
                lineHeight: 1.7,
              }}
            >
              We stand behind this guarantee because we've delivered this transformation{' '}
              <Box component="span" sx={{ color: '#1EB980', fontWeight: 700 }}>
                dozens of times
              </Box>
              . We trust our process. We trust our team. And we're betting on your success.
            </Typography>

            {/* Team Behind the Guarantee - Circular Photos */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                mb: 5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {[
                  'https://images.unsplash.com/photo-1758524056096-6bedf35a33f4?w=200',
                  'https://images.unsplash.com/photo-1600275669283-4bf2bb8a990c?w=200',
                  'https://images.unsplash.com/photo-1598268012815-ae21095df31b?w=200',
                  'https://images.unsplash.com/photo-1589114207353-1fc98a11070b?w=200',
                  'https://images.unsplash.com/photo-1762522927402-f390672558d8?w=200',
                ].map((photo, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: { xs: 44, md: 52 },
                      height: { xs: 44, md: 52 },
                      borderRadius: '50%',
                      border: '3px solid',
                      borderColor: 'background.default',
                      overflow: 'hidden',
                      ml: index > 0 ? -1.5 : 0,
                      position: 'relative',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px) scale(1.1)',
                        zIndex: 10,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={photo}
                      alt={`Team member ${index + 1}`}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                ))}
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: '0.85rem', md: '0.95rem' },
                  color: 'text.secondary',
                  fontWeight: 600,
                  ml: 1,
                }}
              >
                Your dedicated advisory team
              </Typography>
            </Box>

            {/* CTA Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <Box
                component={Link}
                to="/contact"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 2,
                  bgcolor: '#1EB980',
                  color: 'white',
                  px: 5,
                  py: 2.5,
                  borderRadius: '14px',
                  textDecoration: 'none',
                  fontWeight: 800,
                  fontSize: '1.15rem',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 8px 32px rgba(30, 185, 128, 0.3)',
                  position: 'relative',
                  overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                  transition: 'left 0.5s ease',
                },
                '&:hover': {
                  bgcolor: '#179e6b',
                  transform: 'translateY(-4px) scale(1.02)',
                  boxShadow: '0 16px 48px rgba(30, 185, 128, 0.4)',
                  '&::before': {
                    left: '100%',
                  },
                },
              }}
            >
              Book Your Free Consultation
              <ArrowRight size={24} strokeWidth={3} />
              </Box>
            </Box>

            {/* Trust Badge */}
            <Typography 
              sx={{ 
                color: 'text.secondary', 
                fontSize: '0.9rem',
                opacity: 0.8,
                mt: 3,
                fontStyle: 'italic',
                textAlign: 'center',
              }}
            >
              30-minute strategy call · No commitment required
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Solution Overview */}
      <Box id="how-prod-release-helps" sx={{ bgcolor: 'background.default', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" gutterBottom>
              How Prod Release Helps
            </Typography>
          </Box>
          
          <StageComparisonFrame 
            services={stageServices} 
            onServiceClick={(href) => navigate(href)} 
          />
        </Container>
      </Box>

      {/* Meet Our Experts Section */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 8, md: 12 }, position: 'relative', overflow: 'hidden' }}>
        {/* Background Decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '150%',
            height: '150%',
            background: 'radial-gradient(circle at center, rgba(30, 185, 128, 0.05) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" gutterBottom>
              Meet Your Advisory Team
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Real engineering leaders with decades of combined experience building and scaling startups. Not consultants. Not theorists. Battle-tested practitioners.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: 4,
              mb: 6,
            }}
          >
            {[
              {
                name: 'Alex Rivera',
                role: 'Fractional CTO',
                expertise: 'Architecture & Scaling',
                photo: 'https://images.unsplash.com/photo-1758524056096-6bedf35a33f4?w=400',
                description: '15+ years building engineering teams from 2 to 50+ engineers. Led 3 Series A+ startups.',
              },
              {
                name: 'Sarah Chen',
                role: 'VP of Engineering',
                expertise: 'Process & Delivery',
                photo: 'https://images.unsplash.com/photo-1600275669283-4bf2bb8a990c?w=400',
                description: 'Transformed delivery velocity at 5 startups. Expert in Agile, DevOps, and team building.',
              },
              {
                name: 'Marcus Johnson',
                role: 'Principal Engineer',
                expertise: 'Technical Leadership',
                photo: 'https://images.unsplash.com/photo-1598268012815-ae21095df31b?w=400',
                description: 'Built scalable systems serving 100M+ users. AWS & infrastructure specialist.',
              },
            ].map((expert, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  overflow: 'hidden',
                  borderRadius: '20px',
                  bgcolor: 'rgba(30, 185, 128, 0.03)',
                  border: '1px solid',
                  borderColor: 'rgba(30, 185, 128, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(30, 185, 128, 0.2)',
                    borderColor: 'rgba(30, 185, 128, 0.3)',
                  },
                }}
              >
                {/* Photo */}
                <Box
                  sx={{
                    width: '100%',
                    height: 280,
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: 'rgba(30, 185, 128, 0.05)',
                  }}
                >
                  <Box
                    component="img"
                    src={expert.photo}
                    alt={expert.name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                      },
                    }}
                  />
                  {/* Gradient Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '40%',
                      background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent)',
                    }}
                  />
                  {/* Expertise Badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      px: 2,
                      py: 0.75,
                      borderRadius: '8px',
                      bgcolor: 'rgba(30, 185, 128, 0.95)',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        color: 'white',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {expert.expertise}
                    </Typography>
                  </Box>
                </Box>

                {/* Content */}
                <Box sx={{ p: 3 }}>
                  <Typography
                    sx={{
                      fontSize: '1.25rem',
                      fontWeight: 800,
                      mb: 0.5,
                      color: 'text.primary',
                    }}
                  >
                    {expert.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      color: '#1EB980',
                      mb: 2,
                    }}
                  >
                    {expert.role}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: 'text.secondary',
                      lineHeight: 1.6,
                    }}
                  >
                    {expert.description}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>

          {/* Team Stats */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 4,
              justifyContent: 'center',
              alignItems: 'center',
              mt: 8,
              p: 4,
              borderRadius: '16px',
              bgcolor: 'rgba(30, 185, 128, 0.05)',
              border: '1px solid rgba(30, 185, 128, 0.2)',
            }}
          >
            {[
              { number: '50+', label: 'Startups Advised' },
              { number: '200+', label: 'Engineers Hired' },
              { number: '$500M+', label: 'Capital Raised by Clients' },
            ].map((stat, index) => (
              <Box
                key={index}
                sx={{
                  textAlign: 'center',
                  flex: 1,
                  borderRight: { xs: 'none', md: index < 2 ? '1px solid' : 'none' },
                  borderColor: 'rgba(30, 185, 128, 0.2)',
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    fontWeight: 900,
                    background: 'linear-gradient(135deg, #1EB980 0%, #22c997 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    mb: 1,
                  }}
                >
                  {stat.number}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                  }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Problem Statement */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" gutterBottom>
              Startup Engineering Challenges We Address
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ maxWidth: 1100, mx: 'auto' }}
            >
              Behind every great startup is strong engineering but that&apos;s where most teams struggle. From the first MVP to scaling the product, critical gaps appear at every stage. These are the challenges we help startups overcome every day.
            </Typography>
          </Box>

          {/* Before & After Comparison */}
          <Box sx={{ mb: 10, maxWidth: 1000, mx: 'auto' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 4,
              }}
            >
              {/* Before */}
              <Box
                sx={{
                  p: 4,
                  borderRadius: '16px',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
                  border: '2px solid rgba(239, 68, 68, 0.2)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    bgcolor: 'rgba(239, 68, 68, 0.1)',
                    filter: 'blur(40px)',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: '#EF4444',
                    mb: 3,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                  }}
                >
                  Before
                </Typography>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  {[
                    'Missed deadlines and unpredictable delivery',
                    'Technical debt piling up uncontrollably',
                    'No clear engineering processes or structure',
                    'Slow hiring with wrong team composition',
                    'Architecture decisions made without expertise',
                    'Engineering and product teams misaligned',
                  ].map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        mb: 2.5,
                        opacity: 0.8,
                      }}
                    >
                      <XCircle
                        size={20}
                        strokeWidth={2.5}
                        color="#EF4444"
                        style={{ flexShrink: 0, marginTop: 2 }}
                      />
                      <Typography
                        sx={{
                          color: 'text.primary',
                          fontSize: '0.95rem',
                          lineHeight: 1.6,
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* After */}
              <Box
                sx={{
                  p: 4,
                  borderRadius: '16px',
                  bgcolor: 'rgba(30, 185, 128, 0.05)',
                  border: '2px solid rgba(30, 185, 128, 0.3)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    bgcolor: 'rgba(30, 185, 128, 0.1)',
                    filter: 'blur(40px)',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: '#1EB980',
                    mb: 3,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                  }}
                >
                  After
                </Typography>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  {[
                    'Predictable sprints with consistent delivery',
                    'Clean architecture that scales efficiently',
                    'Proven processes and engineering best practices',
                    'Strategic hiring with expert interview support',
                    'Senior-level architecture and technical decisions',
                    'Seamless collaboration across all teams',
                  ].map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        mb: 2.5,
                      }}
                    >
                      <CheckCircle2
                        size={20}
                        strokeWidth={2.5}
                        color="#1EB980"
                        style={{ flexShrink: 0, marginTop: 2 }}
                      />
                      <Typography
                        sx={{
                          color: 'text.primary',
                          fontSize: '0.95rem',
                          lineHeight: 1.6,
                          fontWeight: 500,
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Transformation Statement */}
            <Box
              sx={{
                mt: 5,
                p: 3,
                textAlign: 'center',
                borderRadius: '12px',
                background: 'linear-gradient(90deg, rgba(30, 185, 128, 0.1) 0%, rgba(85, 255, 225, 0.05) 50%, rgba(30, 185, 128, 0.1) 100%)',
              }}
            >
              <Typography
                sx={{
                  color: 'text.primary',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                }}
              >
                We transform chaotic engineering into{' '}
                <Box component="span" sx={{ color: '#1EB980', fontWeight: 700 }}>
                  high-performing delivery engines
                </Box>
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ bgcolor: 'background.default', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="xl">
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
              gap: { xs: 6, md: 8 },
              alignItems: 'start',
            }}
          >
            {/* Left Column - Header and Contact Info */}
            <Box sx={{ position: { md: 'sticky' }, top: { md: 100 } }}>
              <Typography variant="h2" gutterBottom>
                Frequently Asked Questions
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
                Get answers to the most common questions about working with prodrelease
              </Typography>

              {/* Contact Support Box */}
              <Box
                sx={{
                  p: 3,
                  borderRadius: '16px',
                  bgcolor: 'rgba(30, 185, 128, 0.05)',
                  border: '1px solid rgba(30, 185, 128, 0.2)',
                  mt: 4,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    mb: 2,
                    color: 'text.primary',
                  }}
                >
                  Have more questions?
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    color: 'text.secondary',
                    mb: 3,
                    lineHeight: 1.6,
                  }}
                >
                  Contact our support team to get what you need.
                </Typography>
                <Box
                  component="a"
                  href="mailto:support@prodrelease.com"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1.5,
                    color: '#1EB980',
                    textDecoration: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      gap: 2,
                      color: '#179e6b',
                    },
                  }}
                >
                  <Mail size={20} strokeWidth={2.5} />
                  support@prodrelease.com
                </Box>
              </Box>
            </Box>

            {/* Right Column - FAQ Accordions */}
            <Box>
            {[
              {
                question: 'How is this different from hiring a full-time CTO?',
                answer: 'A full-time CTO costs $200K-$400K+ annually, plus equity and benefits. With prodrelease, you get senior-level engineering leadership and architecture expertise at a fraction of the cost. We work fractionally, which means you only pay for what you need when you need it. Plus, we bring a diverse team with experience across multiple startups and industries, giving you more breadth of knowledge than any single hire could provide.',
              },
              {
                question: 'What stage startups do you work with?',
                answer: 'We work with startups from pre-seed to Series A. Whether you\'re just validating your MVP, scaling your engineering team, or preparing for your next funding round, we tailor our services to your specific stage and needs. Our three main service tiers (Foundation & Team Build, Streamline Development Process, and Audit for Best Practices) are designed to address the unique challenges at each stage of your growth.',
              },
              {
                question: 'How quickly can we get started?',
                answer: 'We can typically kick off within 1-2 weeks of our initial consultation. The process is simple: book a free 30-minute strategy call, we\'ll assess your needs and create a custom engagement plan, and then we hit the ground running. For urgent needs, we can sometimes start even sooner.',
              },
              {
                question: 'What does the 30-45 day guarantee actually mean?',
                answer: 'We guarantee that within 30-45 days, you\'ll have a functioning engineering system in place with clear processes, improved delivery velocity, and measurable progress on your technical roadmap. If we don\'t deliver on this promise, we keep working at no additional cost until we do. We\'ve successfully delivered this transformation dozens of times, and we stand behind our work.',
              },
              {
                question: 'How do you measure success?',
                answer: 'We track concrete metrics that matter to your business: sprint velocity and delivery consistency, time-to-hire for engineering roles, code quality and technical debt reduction, system uptime and performance, team satisfaction and retention, and ultimately, your ability to meet product milestones. We establish baseline metrics at the start and provide regular progress reports throughout our engagement.',
              },
              {
                question: 'Can you help us hire engineers?',
                answer: 'Absolutely! We provide comprehensive hiring support including defining role requirements and ideal candidate profiles, conducting technical interviews and assessments, evaluating architecture skills and cultural fit, and negotiating offers and compensation packages. We\'ve helped our clients hire 200+ engineers and have a proven process for finding the right talent quickly.',
              },
              {
                question: 'What if we already have a technical co-founder or CTO?',
                answer: 'Perfect! We work alongside your existing technical leadership to provide additional expertise, bandwidth, and perspective. Many of our clients have technical co-founders who are deep in product development and need strategic support on architecture, scaling, hiring, or processes. We\'re not here to replace anyone—we\'re here to amplify your team\'s capabilities.',
              },
              {
                question: 'What technologies and tech stacks do you work with?',
                answer: 'Our team has deep experience across modern tech stacks including cloud platforms (AWS, GCP, Azure), backend frameworks (Node.js, Python, Go, Ruby, Java), frontend technologies (React, Vue, Angular, Next.js), databases (PostgreSQL, MongoDB, Redis, DynamoDB), and DevOps tools (Docker, Kubernetes, CI/CD pipelines). But more importantly, we focus on helping you choose the right stack for your specific needs, not pushing any particular technology.',
              },
              {
                question: 'What\'s the typical engagement length?',
                answer: 'It varies by service tier and your needs. Foundation & Team Build engagements typically run 4-8 weeks, Streamline Development Process is usually 3-6 months, and Audit for Best Practices takes 6-12 weeks. However, many clients start with one engagement and then continue working with us in different capacities as they grow. We\'re flexible and can adjust based on your evolving needs.',
              },
            ].map((faq, index) => (
              <Box
                key={index}
                sx={{
                  bgcolor: 'background.paper',
                  mb: 2,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: expandedFaq === index ? 'rgba(30, 185, 128, 0.3)' : 'rgba(30, 185, 128, 0.1)',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  boxShadow: expandedFaq === index ? '0 4px 20px rgba(30, 185, 128, 0.15)' : 'none',
                }}
              >
                {/* Question Header - Clickable */}
                <Box
                  onClick={() => toggleFaq(index)}
                  sx={{
                    px: 3,
                    py: 2,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(30, 185, 128, 0.05)',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      color: 'text.primary',
                    }}
                  >
                    {faq.question}
                  </Typography>
                  <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                    {expandedFaq === index ? (
                      <ChevronUp size={24} color="#1EB980" />
                    ) : (
                      <ChevronDown size={24} color="#1EB980" />
                    )}
                  </Box>
                </Box>

                {/* Answer - Conditionally Rendered */}
                {expandedFaq === index && (
                  <Box
                    sx={{
                      px: 3,
                      pb: 3,
                      pt: 0,
                      borderTop: '1px solid',
                      borderColor: 'rgba(30, 185, 128, 0.1)',
                      animation: 'fadeIn 0.3s ease',
                      '@keyframes fadeIn': {
                        from: { opacity: 0, transform: 'translateY(-10px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.7,
                        fontSize: '0.95rem',
                      }}
                    >
                      {faq.answer}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
            </Box>
          </Box>

          {/* CTA after FAQ */}
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Typography
              sx={{
                fontSize: '1.2rem',
                fontWeight: 600,
                mb: 3,
                color: 'text.primary',
              }}
            >
              Still have questions?
            </Typography>
            <Box
              component={Link}
              to="/contact"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: '#1EB980',
                color: 'white',
                px: 4,
                py: 2,
                borderRadius: '12px',
                textDecoration: 'none',
                fontWeight: 700,
                fontSize: '1rem',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 20px rgba(30, 185, 128, 0.3)',
                '&:hover': {
                  bgcolor: '#179e6b',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 30px rgba(30, 185, 128, 0.4)',
                },
              }}
            >
              Schedule a Free Consultation
              <ArrowRight size={20} strokeWidth={2.5} />
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}